import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * Speaking a destination instead of typing one.
 *
 * Two implementations behind one call, and neither is the obvious one.
 *
 * On a phone this talks to `DictationPlugin`, written into the app target
 * (see ios/App/App/DictationPlugin.swift). The ready-made package,
 * @capacitor-community/speech-recognition, ships a podspec and no Package.swift
 * — this is an SPM project, so `cap sync` skips it with a warning and the
 * plugin never exists at runtime.
 *
 * The Web Speech API cannot cover for it either: WKWebView does not implement
 * SpeechRecognition, whatever Safari on the same phone manages. It is kept for
 * the buggy head unit and for development, where it genuinely works.
 */

type Listener = (text: string, final: boolean) => void;

interface DictationPlugin {
  available(): Promise<{ available: boolean }>;
  start(): Promise<void>;
  stop(): Promise<void>;
  addListener(
    event: 'partial',
    cb: (data: { text: string; final: boolean }) => void
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

const Native = registerPlugin<DictationPlugin>('Dictation');

interface WebSpeechCtor {
  new (): WebSpeechRecognition;
}
interface WebSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  abort: () => void;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

function webCtor(): WebSpeechCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: WebSpeechCtor;
    webkitSpeechRecognition?: WebSpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const isNative = () => Capacitor.isNativePlatform();

/** Whether a microphone button can do anything here. Governs whether it shows. */
export async function dictationAvailable(): Promise<boolean> {
  if (isNative()) {
    try {
      return (await Native.available()).available;
    } catch {
      return false;
    }
  }
  return webCtor() !== null;
}

export interface Dictation {
  stop: () => Promise<void>;
}

/**
 * Starts listening. Resolves once the microphone is actually live, so the
 * caller shows the recording state only when there is something to record.
 *
 * Partial results arrive as they are spoken — a search that fills in while you
 * are still talking is the difference between instant and a form submission.
 */
export async function startDictation(onText: Listener): Promise<Dictation> {
  if (isNative()) {
    await Native.removeAllListeners();
    const handle = await Native.addListener('partial', (d) => onText(d.text ?? '', !!d.final));
    try {
      await Native.start();
    } catch (e) {
      await handle.remove();
      throw e;
    }
    return {
      stop: async () => {
        try {
          await Native.stop();
        } finally {
          await handle.remove();
        }
      },
    };
  }

  const Ctor = webCtor();
  if (!Ctor) throw new Error('This device cannot listen');
  const rec = new Ctor();
  rec.lang = 'en-AU';
  rec.continuous = false;
  rec.interimResults = true;
  rec.onresult = (e) => {
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      const r = e.results[i];
      onText(r[0].transcript, r.isFinal);
    }
  };
  rec.onend = () => onText('', true);
  rec.onerror = () => onText('', true);
  rec.start();

  return { stop: async () => rec.abort() };
}
